import AreaController, {
  AreaPageProps,
} from "../../../controller/area.controller";
import { GetServerSideProps, NextPage } from "next";
import useQuery from "../../../hooks/useQuery";
import React, { Suspense, useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Head from "next/head";
import VerifyModal from "../../../components/VerifyModal";
import useStore from "../../../context/useStore";

const NotListing = dynamic(
  () => import("../../../components/Listing/NotListing"),
  {
    suspense: true,
  }
);

const Card = dynamic(() => import("../../../components/Card"), {
  suspense: true,
});

const Pagination = dynamic(() => import("rc-pagination"), { suspense: true });

const Filters = dynamic(() => import("../../../components/Listing/Filters"), {
  suspense: true,
});

const Area: NextPage<AreaPageProps> = ({ query, area, properties }) => {
  const queries = useQuery(query);
  const [visible, setVisible] = useState(false);
  const { verify, setVerify } = useStore();

  const onClose = () => {
    setVerify({
      open: false,
      closable: true,
    });
  };
  useEffect(() => {
    setVisible(verify.open);
  }, [verify.open]);

  return (
    <>
      <Head>
        <title>{area.title}</title>
        <meta
          name="title"
          property="og:title"
          content={(area.title && area.title) || ""}
        />

        <meta
          name="description"
          property="og:description"
          content={(area.desc && area.desc) || ""}
        />

        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`https://bungalowfinder.ca/area/${area.slug}`}
        />

        <meta
          property="og:image"
          itemProp="image"
          content={`https://api.bungalowfinder.ca` + area.image?.images.og.url}
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content={(area.title && area.title) || ""}
        />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={(area.title && area.title) || ""} />
        <meta
          name="twitter:description"
          content={(area.desc && area.desc) || ""}
        />
        <meta
          name="twitter:image"
          content={`https://api.bungalowfinder.ca` + area.image?.images.og.url}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `${(area.jsonLD && area.jsonLD) || ""}`,
          }}
        ></script>
      </Head>
      <div className="flex-1 flex flex-col">
        <div className="bg-primary z-10">
          <div className="bg-primary mix-blend-screen text-white py-3">
            <div className="container mx-auto flex justify-end">
              <Suspense
                fallback={
                  <>
                    <p>Loading...</p>
                  </>
                }
              >
                <Filters query={query} queries={queries} />
              </Suspense>
            </div>
          </div>
        </div>
        <div className="flex flex-col flex-1 container mx-auto w-full">
          <div className="overflow-hidden flex-1 2xl:flex-none">
            <div className="flex md:justify-between justify-center flex-wrap items-center">
              <div className="px-2 py-3 flex justify-center md:justify-between items-center flex-wrap">
                <h1 className="font-medium line-clamp-1 text-xl">
                  Bungalows for sale in {area.label}, {properties.total}{" "}
                  Listings
                </h1>
              </div>
              <div className="px-2">
                <label>
                  Sort by:
                  <select
                    onChange={(e) => {
                      queries.push({
                        sortby: e.currentTarget.value,
                      });
                    }}
                    defaultValue={query.sortby}
                    className="bg-transparent w-24 whitespace-nowrap"
                  >
                    <option value="lts">New Listings</option>
                    <option value="ols">Oldest Listings</option>
                    <option value="lth">Price-Low To High Listings</option>
                    <option value="htl">Price-High To Low Listings</option>
                  </select>
                </label>
              </div>
            </div>

            <Suspense
              fallback={
                <>
                  <p>Loading...</p>
                </>
              }
            >
              {properties.property.length !== 0 ? (
                <div className="px-2 py-1 grid overflow-hidden grid-cols-1 xl:grid-cols-4 gap-2">
                  {properties.property.map((property, index) => (
                    <Card key={index} property={property} />
                  ))}
                </div>
              ) : (
                <>
                  <Suspense
                    fallback={
                      <>
                        <p>Loading...</p>
                      </>
                    }
                  >
                    <NotListing />
                  </Suspense>
                </>
              )}
            </Suspense>
            <div className="px-2 py-5 text-center">
              <Suspense
                fallback={
                  <>
                    <p>Loading...</p>
                  </>
                }
              >
                <Pagination
                  total={properties.total}
                  pageSize={12}
                  defaultCurrent={query.page}
                  onChange={(page) => {
                    queries.push({
                      page,
                    });
                  }}
                />
              </Suspense>
            </div>
            <hr />
            <div
              className="px-2 py-5 element"
              dangerouslySetInnerHTML={{
                __html: (area.content && area.content) || "",
              }}
            />
          </div>
        </div>
      </div>
      <VerifyModal open={visible} onClose={onClose} />
    </>
  );
};

export default Area;

export const getServerSideProps: GetServerSideProps = async (context) => {
  return await AreaController(context);
};
